import {
  Component, ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding, inject,
  Input,
  OnChanges,
  Output,
  TemplateRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { v4 as uuid } from 'uuid';
import { NgTemplateOutlet } from '@angular/common';
import { ErrorTranslateService } from '../error-translate-service';
import { BaseFormErrorTranslateService } from '../form-common-error/base-form-error-translate.service';
import { BrandsEnum } from '../../../_enums/brands.enum';
import { BrandThemesEnum, ThemesEnum } from '../../../_enums/themes.enum';
import { IMedia } from '../../../_interfaces/media.interface';
import { SdLibConfig } from '../../../sd-lib-config';
import { SdBannerComponent } from '../../medias/banner/banner.component';
import { AnimationsEnum } from '../../../_enums/animations.enum';
import { SdHtmlComponent } from '../../html/html.component';

@Component({
  selector: 'sd-form[checkbox]',
  templateUrl: './form-checkbox.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SdFormCheckboxComponent),
      multi: true
    },
    { provide: BaseFormErrorTranslateService, useClass: ErrorTranslateService }
  ],
  standalone: true,
  imports: [
    NgTemplateOutlet,
    SdBannerComponent,
    SdHtmlComponent
  ]
})
export class SdFormCheckboxComponent implements OnChanges, ControlValueAccessor {
  @Input()
  brand: BrandsEnum;

  @Input()
  labelTitle: string;

  @Input()
  nameField: string;

  @Input()
  checked = false;

  @Output()
  checkedChange = new EventEmitter<boolean>();

  @Input()
  labelHidden = false;

  @Input()
  isRadio = false;

  @Input()
  required = false;

  @Input()
  media: IMedia;

  @Input()
  readonly = false;

  @Input()
  disabled = false;

  @Input()
  isToggle = false;

  @Input()
  libelleTemplate: TemplateRef<any>;

  @Input()
  theme: ThemesEnum | BrandThemesEnum;

  @Input()
  customClass: string;

  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.isRadio ? 'is-radio' : '',
      this.isToggle ? 'is-toggle' : '',
      this.animation ? 'is-animation-' + this.animation : '',
      this.theme ? 'is-theme-' + this.theme : '',
      this.libelleTemplate ? 'has-custom-template' : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  get value(): any {
    return this.checked;
  }

  public readonly elementRef = inject(ElementRef);
  private readonly sdLibConfig = inject(SdLibConfig);

  animation: AnimationsEnum;
  uuidInput: string = uuid();
  currentValue: any;

  constructor() {
    this.animation = this.sdLibConfig.get().animation;
  }

  ngOnChanges() {
    this.animation = this.sdLibConfig.get(this.brand).animation;
  }

  /**
   * Function that will be called when changing values.
   * Can be modified via registerOnChanged.
   */
  // tslint: no-empty-function
  onChange(_: any): void {
    // empty
  }

  // tslint: no-empty-function
  onTouched(): void {
    // empty
  }

  /**
   * Function called when the user clicks on the checkbox
   */
  clicked() {
    if (!this.disabled && !this.readonly) {
      const wasChecked = this.checked;
      this.checked = !wasChecked;
      this.checkedChange.emit(this.value);
      this.onChange(this.value);
    }
  }

  /**
   * Allow angular to update the model
   * @param newValue new value
   */
  writeValue(newValue: any): void {
    this.checked = newValue;
    this.currentValue = newValue.toString();
  }

  /**
   * Saves a function to be called when the template is updated
   */
  registerOnChange(fn: (_: any) => void): void {
    this.onChange = fn;
  }

  /**
   * Saves a function to be called when the component is "touched"
   * Currently unmanaged
   */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Allows angular to disable the component
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
